'use client'

import Link from 'next/link'
import { HugeiconsIcon } from '@hugeicons/react'
import { ArrowUpRight01Icon, Cancel01Icon } from '@hugeicons-pro/core-stroke-rounded'
import {
  Dialog,
  DialogClose,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from '@/components/ui/dialog'
import { Dialog as DialogPrimitive } from 'radix-ui'
import type {
  CatalogChannelView,
  CatalogSubchannelView,
} from '@/lib/connectors/get-catalog'
import { sortSubchannelsForGrid } from '@/lib/connectors/sort-subchannels-for-grid'
import { cn } from '@/lib/utils'
import { SubchannelCard } from './SubchannelCard'
import { channelGradientFor, channelIconFor } from './channel-visuals'

export function ChannelOverlay({
  channel,
  open,
  onOpenChange,
  onOpenSubchannel,
}: {
  channel: CatalogChannelView | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onOpenSubchannel: (sub: CatalogSubchannelView) => void
}) {
  const Icon = channel ? channelIconFor(channel.key) : channelIconFor('search')
  const gradient = channel ? channelGradientFor(channel.key) : channelGradientFor('search')
  const subchannels = channel?.subchannels.filter((sub) => sub.status !== 'deprecated') ?? []
  const connected = subchannels.filter(
    (sub) =>
      sub.connection?.status === 'connected' || sub.connection?.status === 'pending',
  ).length

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className="bg-black/45 backdrop-blur-[2px]" />
        <DialogPrimitive.Content
          className="fixed top-4 right-4 bottom-4 left-4 z-[120] flex translate-x-0 translate-y-0 flex-col gap-0 overflow-hidden rounded-2xl border border-line bg-canvas p-0 text-text-strong shadow-2xl outline-none sm:top-6 sm:right-6 sm:bottom-6 sm:left-6 lg:top-8 lg:bottom-8 lg:left-1/2 lg:w-full lg:max-w-3xl lg:-translate-x-1/2 lg:right-auto"
        >
          <DialogClose className="absolute top-4 right-4 z-10 grid size-8 place-items-center rounded-full bg-black/25 text-white backdrop-blur-sm transition-colors hover:bg-black/40 focus:outline-none">
            <HugeiconsIcon icon={Cancel01Icon} className="size-4" />
            <span className="sr-only">Close</span>
          </DialogClose>
        {channel && (
          <div className="flex h-full min-h-0 flex-col overflow-y-auto">
            <section
              className={cn(
                'relative flex min-h-[40dvh] flex-col justify-end overflow-hidden px-6 pb-9 pt-20 text-white sm:px-10 lg:px-14',
                gradient,
              )}
            >
              <div className="absolute left-6 top-6 sm:left-10 lg:left-14">
                <span className="grid size-16 place-items-center rounded-[18px] bg-white/25 sm:size-20">
                  <HugeiconsIcon icon={Icon} className="size-8 text-white sm:size-10" aria-hidden />
                </span>
              </div>
              <div className="max-w-4xl">
                <div className="mb-3 font-mono text-[12px] font-semibold uppercase tracking-[0.12em] text-white/85">
                  {connected} of {subchannels.length} connected
                </div>
                <DialogTitle className="text-[44px] font-bold leading-[0.98] tracking-[-0.03em] text-white sm:text-[72px]">
                  {channel.name}
                </DialogTitle>
                {channel.description && (
                  <DialogDescription className="mt-5 max-w-[660px] text-[15px] leading-[1.6] text-white/82 sm:text-base">
                    {channel.description}
                  </DialogDescription>
                )}
              </div>
            </section>

            <section className="mx-auto flex w-full max-w-6xl flex-1 flex-col gap-5 px-6 py-8 sm:px-10 lg:px-14">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <div className="font-mono text-[11px] font-semibold uppercase tracking-[0.12em] text-brand">
                    Connectors
                  </div>
                  <h2 className="mt-1 text-[22px] font-bold tracking-[-0.02em]">
                    Available in {channel.name}
                  </h2>
                </div>
                <Link
                  href={`/channels/${channel.key}`}
                  className="inline-flex h-10 shrink-0 items-center justify-center gap-1.5 rounded-[10px] bg-brand px-4 text-sm font-semibold text-white transition-colors hover:bg-brand-deep"
                >
                  View full channel
                  <HugeiconsIcon icon={ArrowUpRight01Icon} className="size-4" aria-hidden />
                </Link>
              </div>

              {subchannels.length === 0 ? (
                <div className="rounded-card border-[0.5px] border-line bg-surface px-5 py-10 text-center text-sm text-text-muted">
                  No connectors are available for this channel yet.
                </div>
              ) : (
                <div className="grid grid-cols-[repeat(auto-fill,minmax(244px,1fr))] gap-3">
                  {sortSubchannelsForGrid(subchannels).map((sub) => (
                    <SubchannelCard
                      key={sub.key}
                      sub={sub}
                      channelKey={channel.key}
                      onOpen={onOpenSubchannel}
                    />
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  )
}
